import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search, Download, Wallet, TrendingUp, TrendingDown, Scale, Tags, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageUpload from "@/components/admin/ImageUpload";
import api, { apiError, fileUrl } from "@/lib/api";
import { localDateISO } from "@/lib/utils";
import { formatDate } from "@/components/public/Shared";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const PAYMENT_METHODS = ["Cash", "UPI", "Bank Transfer", "Cheque", "Other"];

const EMPTY_ENTRY = {
  kind: "income", date: "", ref_no: "", category: "", subcategory: "",
  description: "", amount: "", payment_method: "Cash", party: "", notes: "", attachment_url: "",
  pledge_no: "", family_id: "", pledge_months: [], backdated_reason: "",
};

function monthsInRange(from, to) {
  if (!from || !to || from > to) return [];
  const out = [];
  let y = parseInt(from.slice(0, 4), 10);
  let m = parseInt(from.slice(5, 7), 10);
  const ty = parseInt(to.slice(0, 4), 10);
  const tm = parseInt(to.slice(5, 7), 10);
  while (y < ty || (y === ty && m <= tm)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
    if (out.length > 60) break;
  }
  return out;
}

function monthLabel(m) {
  if (!m) return "";
  return new Date(m + "-01T00:00:00").toLocaleDateString("en-IN", { month: "short", year: "numeric" });
}

function periodRange(period) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const iso = localDateISO;
  if (period === "this_month") return [iso(new Date(y, m, 1)), iso(new Date(y, m + 1, 0))];
  if (period === "last_month") return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
  if (period === "this_quarter") {
    const qs = Math.floor(m / 3) * 3;
    return [iso(new Date(y, qs, 1)), iso(new Date(y, qs + 3, 0))];
  }
  if (period === "this_year") return [`${y}-01-01`, `${y}-12-31`];
  return ["", ""];
}

export default function Ledger() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [report, setReport] = useState(null);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [openingOpen, setOpeningOpen] = useState(false);
  const [period, setPeriod] = useState("this_month");
  const [pledgeOverlap, setPledgeOverlap] = useState([]);
  const pageSize = 15;

  const setPledgeMonths = useCallback((months, overlap) => {
    setForm((f) => ({ ...f, pledge_months: months }));
    setPledgeOverlap(overlap);
  }, []);

  const loadCategories = useCallback(() => {
    api.get("/admin/ledger/categories").then((r) => setCategories(r.data)).catch((e) => toast.error(apiError(e, "Could not load ledger data")));
  }, []);

  const loadSummary = useCallback(() => {
    api.get("/admin/ledger/summary").then((r) => setSummary(r.data)).catch((e) => toast.error(apiError(e, "Could not load ledger data")));
    api.get("/admin/ledger/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(apiError(e, "Could not load ledger data")));
  }, []);

  const load = useCallback(() => {
    const params = { page, page_size: pageSize };
    if (tab !== "all") params.kind = tab;
    if (q) params.q = q;
    if (category !== "all") params.category = category;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get("/admin/ledger/entries", { params }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [tab, q, category, dateFrom, dateTo, page]);

  useEffect(() => { loadCategories(); loadSummary(); }, [loadCategories, loadSummary]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const loadReport = useCallback(() => {
    const [from, to] = periodRange(period);
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    api.get("/admin/ledger/summary", { params }).then((r) => setReport(r.data)).catch((e) => toast.error(apiError(e, "Could not load ledger data")));
  }, [period]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const filteredCategories = useMemo(
    () => categories.filter((c) => form.kind === "all" || c.type === form.kind),
    [categories, form.kind]
  );
  const filterCategoryOptions = useMemo(() => {
    if (tab === "income") return categories.filter((c) => c.type === "income");
    if (tab === "expense") return categories.filter((c) => c.type === "expense");
    return categories;
  }, [categories, tab]);
  const allCatLabel = tab === "income" ? "All Income Categories" : tab === "expense" ? "All Expense Categories" : "All Categories";
  const incomeCats = useMemo(
    () => (report ? report.by_category.filter((c) => c.kind === "income") : []),
    [report]
  );
  const expenseCats = useMemo(
    () => (report ? report.by_category.filter((c) => c.kind === "expense") : []),
    [report]
  );

  const openCreate = (kind) => {
    setForm({ ...EMPTY_ENTRY, kind, date: localDateISO() });
    setPledgeOverlap([]);
    setEditing("new");
  };
  const openEdit = (e) => {
    setForm({
      kind: e.kind, date: e.date || "", ref_no: e.ref_no || "", category: e.category || "",
      subcategory: e.subcategory || "", description: e.description || "", amount: e.amount ?? "",
      payment_method: e.payment_method || "Cash", party: e.party || "", notes: e.notes || "",
      attachment_url: e.attachment_url || "", pledge_no: e.pledge_no || "", family_id: e.family_id || "",
      pledge_months: e.pledge_months || [], backdated_reason: e.backdated_reason || "",
    });
    setPledgeOverlap([]);
    setEditing(e);
  };

  const save = async () => {
    if (!form.date) { toast.error("Date is required"); return; }
    if (!form.category) { toast.error("Please choose a category"); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    if (form.kind === "income" && !form.party.trim()) { toast.error("Donor Name is required for income"); return; }
    if (isPledgeIncome) {
      if (!form.pledge_months || form.pledge_months.length === 0) { toast.error("Select the pledge month(s) this payment covers"); return; }
      if (pledgeOverlap.length > 0) { toast.error(`${pledgeOverlap.map(monthLabel).join(", ")} already recorded — adjust the pledge months`); return; }
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/ledger/entries", { ...form, amount: Number(form.amount) });
        toast.success(`${form.kind === "income" ? "Income" : "Expense"} recorded`);
      } else {
        await api.put(`/admin/ledger/entries/${editing.id}`, { ...form, amount: Number(form.amount) });
        toast.success("Record updated");
      }
      setEditing(null);
      load(); loadSummary(); loadReport();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/ledger/entries/${deleting.id}`);
      toast.success("Record deleted");
      setDeleting(null);
      load(); loadSummary(); loadReport();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get("/admin/ledger/export/csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "church_ledger.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Ledger exported");
    } catch (err) {
      toast.error(apiError(err, "Export failed"));
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  let dialogTitle = "Edit Record";
  if (editing === "new") {
    dialogTitle = form.kind === "income" ? "Add Income" : "Add Expense";
  }
  const isPledgeIncome = form.kind === "income" && /pledge/i.test(form.category || "");
  const todayISO = localDateISO();
  const isBackdated = !!form.date && form.date < todayISO;

  return (
    <div data-testid="ledger-page" className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy">Church Ledger</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Private income &amp; expense records. Financial data never appears on the public website.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setCatOpen(true)} data-testid="ledger-categories-btn">
            <Tags className="mr-2 h-4 w-4" /> Categories
          </Button>
          <Button variant="outline" onClick={() => setOpeningOpen(true)} data-testid="ledger-opening-btn">
            <Scale className="mr-2 h-4 w-4" /> Opening Balance
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="ledger-export-btn">
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          <Button onClick={() => openCreate("income")} className="bg-green-700 text-white hover:bg-green-800" data-testid="ledger-add-income-btn">
            <Plus className="mr-2 h-4 w-4" /> Add Income
          </Button>
          <Button onClick={() => openCreate("expense")} className="bg-navy text-cream hover:bg-navy-light" data-testid="ledger-add-expense-btn">
            <Plus className="mr-2 h-4 w-4" /> Add Expense
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="ledger-summary-cards">
        <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
          <Scale className="h-5 w-5 text-gold-dark" />
          <p className="mt-3 font-heading text-2xl font-bold text-navy" data-testid="ledger-opening-value">{summary ? inr(summary.opening_balance) : "—"}</p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Opening Balance</p>
        </div>
        <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
          <TrendingUp className="h-5 w-5 text-green-700" />
          <p className="mt-3 font-heading text-2xl font-bold text-green-700" data-testid="ledger-income-value">{summary ? inr(summary.total_income) : "—"}</p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total Income</p>
        </div>
        <div className="rounded-lg border border-border bg-white p-5 shadow-sm">
          <TrendingDown className="h-5 w-5 text-destructive" />
          <p className="mt-3 font-heading text-2xl font-bold text-destructive" data-testid="ledger-expense-value">{summary ? inr(summary.total_expense) : "—"}</p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Total Expenses</p>
        </div>
        <div className="rounded-lg border border-gold/40 bg-navy p-5 shadow-sm">
          <Wallet className="h-5 w-5 text-gold" />
          <p className="mt-3 font-heading text-2xl font-bold text-cream" data-testid="ledger-closing-value">{summary ? inr(summary.closing_balance) : "—"}</p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-cream/60">Closing Balance</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setCategory("all"); setPage(1); }}>
            <TabsList className="bg-secondary" data-testid="ledger-tabs">
              <TabsTrigger value="all" data-testid="ledger-tab-all">All</TabsTrigger>
              <TabsTrigger value="income" data-testid="ledger-tab-income">Income</TabsTrigger>
              <TabsTrigger value="expense" data-testid="ledger-tab-expense">Expenses</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative min-w-56 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search description, receipt no, donor/payee…" className="pl-9 bg-white" data-testid="ledger-search" />
          </div>
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
            <SelectTrigger className="w-52 bg-white" data-testid="ledger-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{allCatLabel}</SelectItem>
              {filterCategoryOptions.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}{tab === "all" ? ` (${c.type})` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} className="w-36 bg-white text-xs" aria-label="From date" data-testid="ledger-date-from" />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} className="w-36 bg-white text-xs" aria-label="To date" data-testid="ledger-date-to" />
          </div>
        </div>

        {!data && <p className="text-sm text-muted-foreground">Loading…</p>}
        {data && data.items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border bg-white/60 px-8 py-14 text-center" data-testid="ledger-empty">
            <Wallet className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="mt-3 font-heading text-lg font-bold text-navy">No ledger records yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Start by setting the opening balance, then record income and expenses.</p>
          </div>
        )}
        {data && data.items.length > 0 && (
          <>
            <div className="overflow-x-auto rounded-lg border border-border bg-white shadow-sm">
              <table className="w-full text-sm" data-testid="ledger-table">
                <thead>
                  <tr className="border-b border-border bg-secondary/60 text-left">
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Date</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Type</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Ref No</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Category</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Description</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy/70">Donor / Payee</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-navy/70">Amount</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-navy/70">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.items.map((e) => (
                    <tr key={e.id} className="hover:bg-secondary/30" data-testid={`ledger-row-${e.id}`}>
                      <td className="whitespace-nowrap px-4 py-3 text-foreground/80">{formatDate(e.date)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${e.kind === "income" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                          {e.kind === "income" ? "Income" : "Expense"}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground/70">{e.ref_no || "—"}</td>
                      <td className="px-4 py-3 text-foreground/80">{e.category}{e.subcategory ? ` / ${e.subcategory}` : ""}</td>
                      <td className="max-w-56 truncate px-4 py-3 text-foreground/80" title={e.description}>{e.description || "—"}</td>
                      <td className="px-4 py-3 text-foreground/80">{e.party || "—"}</td>
                      <td className={`whitespace-nowrap px-4 py-3 text-right font-semibold ${e.kind === "income" ? "text-green-700" : "text-destructive"}`}>
                        {e.kind === "income" ? "+" : "−"}{inr(e.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          {e.attachment_url && (
                            <Button variant="ghost" size="icon" asChild aria-label="View receipt">
                              <a href={fileUrl(e.attachment_url)} target="_blank" rel="noopener noreferrer" data-testid={`ledger-receipt-${e.id}`}>
                                <FileText className="h-4 w-4 text-gold-dark" />
                              </a>
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" onClick={() => openEdit(e)} data-testid={`ledger-edit-${e.id}`} aria-label="Edit record">
                            <Pencil className="h-4 w-4 text-navy/60" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleting(e)} data-testid={`ledger-delete-${e.id}`} aria-label="Delete record">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3" data-testid="ledger-pagination">
              <p className="text-xs text-muted-foreground">
                Page {data.page} of {totalPages} · {data.total} records · Filtered totals: <span className="text-green-700 font-semibold">+{inr(data.income_total)}</span> / <span className="text-destructive font-semibold">−{inr(data.expense_total)}</span>
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="ledger-prev-page">Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="ledger-next-page">Next</Button>
              </div>
            </div>
          </>
        )}
      </div>

      <section className="rounded-lg border border-border bg-white p-6 shadow-sm" data-testid="ledger-reports">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h2 className="font-heading text-xl font-bold text-navy">Reports</h2>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-48 bg-white" data-testid="ledger-report-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {report && (
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Opening {inr(report.opening_balance)} + Income {inr(report.total_income)} − Expenses {inr(report.total_expense)} = <span className="font-semibold text-navy">Closing {inr(report.closing_balance)}</span>
            </p>
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-green-700">Income by Category</p>
                <table className="w-full text-sm" data-testid="ledger-report-income">
                  <tbody className="divide-y divide-border">
                    {incomeCats.map((c) => (
                      <tr key={c.category}>
                        <td className="py-2 text-foreground/80">{c.category} <span className="text-xs text-muted-foreground">({c.count})</span></td>
                        <td className="py-2 text-right font-semibold text-green-700">{inr(c.total)}</td>
                      </tr>
                    ))}
                    {incomeCats.length === 0 && (
                      <tr><td className="py-2 text-sm text-muted-foreground">No income in this period.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-destructive">Expenses by Category</p>
                <table className="w-full text-sm" data-testid="ledger-report-expense">
                  <tbody className="divide-y divide-border">
                    {expenseCats.map((c) => (
                      <tr key={c.category}>
                        <td className="py-2 text-foreground/80">{c.category} <span className="text-xs text-muted-foreground">({c.count})</span></td>
                        <td className="py-2 text-right font-semibold text-destructive">{inr(c.total)}</td>
                      </tr>
                    ))}
                    {expenseCats.length === 0 && (
                      <tr><td className="py-2 text-sm text-muted-foreground">No expenses in this period.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {report.by_month.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-navy/70">Monthly Totals</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="ledger-report-monthly">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wider text-navy/70">Month</th>
                        <th className="py-2 pr-4 text-right text-xs font-semibold uppercase tracking-wider text-green-700">Income</th>
                        <th className="py-2 pr-4 text-right text-xs font-semibold uppercase tracking-wider text-destructive">Expenses</th>
                        <th className="py-2 text-right text-xs font-semibold uppercase tracking-wider text-navy/70">Net</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {report.by_month.map((m) => (
                        <tr key={m.month}>
                          <td className="py-2 pr-4 text-foreground/80">{new Date(m.month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</td>
                          <td className="py-2 pr-4 text-right font-semibold text-green-700">{inr(m.income)}</td>
                          <td className="py-2 pr-4 text-right font-semibold text-destructive">{inr(m.expense)}</td>
                          <td className="py-2 text-right font-semibold text-navy">{inr(m.income - m.expense)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="ledger-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy">
              {dialogTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v, category: "" })}>
                  <SelectTrigger className="mt-1.5" data-testid="ledger-field-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="lf-date">Date <span className="text-destructive">*</span></Label>
                <Input id="lf-date" data-testid="ledger-field-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lf-ref">{form.kind === "income" ? "Receipt Number" : "Voucher / Reference No"}</Label>
                <Input id="lf-ref" data-testid="ledger-field-ref" value={form.ref_no} onChange={(e) => setForm({ ...form, ref_no: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="lf-amount">Amount (₹) <span className="text-destructive">*</span></Label>
                <Input id="lf-amount" data-testid="ledger-field-amount" type="number" min="1" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category <span className="text-destructive">*</span></Label>
                <Select value={form.category || undefined} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="ledger-field-category"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {filteredCategories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="lf-subcat">Subcategory</Label>
                <Input id="lf-subcat" data-testid="ledger-field-subcategory" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="lf-desc">Description</Label>
              <Input id="lf-desc" data-testid="ledger-field-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="ledger-field-payment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {form.kind === "expense" && (
                <div>
                  <Label htmlFor="lf-party">Payee</Label>
                  <Input id="lf-party" data-testid="ledger-field-party" value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} className="mt-1.5" />
                </div>
              )}
            </div>
            {form.kind === "income" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lf-family-id">Family ID <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
                  <FamilyIdField
                    value={form.family_id}
                    onType={(v) => setForm((f) => ({ ...f, family_id: v }))}
                    onPick={(fam) => setForm((f) => ({ ...f, family_id: fam.family_id, party: fam.head_of_family || fam.family_name || f.party }))}
                  />
                  {form.family_id ? (
                    <p className="mt-1 text-xs text-green-700" data-testid="ledger-family-linked">Linked to Family Directory</p>
                  ) : (
                    <p className="mt-1 text-xs text-muted-foreground">Leave blank for a non-member donor</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="lf-party">Donor Name <span className="text-destructive">*</span></Label>
                  <DonorNameField
                    value={form.party}
                    onType={(v) => setForm((f) => ({ ...f, party: v }))}
                    onPick={(m) => setForm((f) => ({ ...f, party: m.full_name, family_id: m.family_id || f.family_id }))}
                  />
                </div>
              </div>
            )}
            {isPledgeIncome && (
              <div className="rounded-lg border border-gold/40 bg-gold/5 p-4 space-y-3" data-testid="ledger-pledge-section">
                <div className="flex items-end justify-between gap-3">
                  <Label className="text-navy">Pledge Month(s) <span className="text-destructive">*</span></Label>
                  <div className="w-36">
                    <Label htmlFor="lf-pledge" className="text-xs text-muted-foreground">Pledge No.</Label>
                    <Input id="lf-pledge" data-testid="ledger-field-pledge-no" value={form.pledge_no} onChange={(e) => setForm({ ...form, pledge_no: e.target.value })} className="mt-1 h-8" placeholder="e.g. 37" />
                  </div>
                </div>
                <PledgeMonths
                  familyId={form.family_id}
                  party={form.party}
                  initialMonths={form.pledge_months}
                  excludeId={editing && editing !== "new" ? editing.id : ""}
                  onChange={setPledgeMonths}
                />
              </div>
            )}
            {isBackdated && (
              <div data-testid="ledger-backdated-section">
                <Label htmlFor="lf-backdated">Backdated Entry Reason <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label>
                <Textarea id="lf-backdated" data-testid="ledger-field-backdated-reason" rows={2}
                  value={form.backdated_reason} onChange={(e) => setForm({ ...form, backdated_reason: e.target.value })}
                  className="mt-1.5" placeholder="Why is this entry dated earlier than today?" />
                <p className="mt-1 text-xs text-amber-700">This transaction date is earlier than today ({todayISO}) — a reason is required.</p>
              </div>
            )}
            <div>
              <Label htmlFor="lf-notes">Notes</Label>
              <Textarea id="lf-notes" data-testid="ledger-field-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
            </div>
            {form.kind === "expense" && (
              <div>
                <Label>Supporting Document / Receipt (image)</Label>
                <div className="mt-1.5">
                  <ImageUpload value={form.attachment_url} onChange={(v) => setForm({ ...form, attachment_url: v })} testId="ledger-field-attachment" isPrivate />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="ledger-form-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving || (isPledgeIncome && pledgeOverlap.length > 0)} className="bg-navy text-cream hover:bg-navy-light" data-testid="ledger-form-save">
              {saving ? "Saving…" : "Save Record"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent data-testid="ledger-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the {deleting?.kind} record of {inr(deleting?.amount)}{deleting?.category ? ` (${deleting.category})` : ""}. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ledger-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="ledger-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CategoriesDialog open={catOpen} onClose={() => { setCatOpen(false); loadCategories(); }} categories={categories} />
      <OpeningBalanceDialog open={openingOpen} onClose={() => setOpeningOpen(false)} settings={settings} onSaved={() => { setOpeningOpen(false); loadSummary(); }} />
    </div>
  );
}

function FamilyIdField({ value, onType, onPick }) {
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/admin/ledger/family-picker", { params: { q: value } });
        setOpts(res.data.items || []);
      } catch {
        setOpts([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value, open]);
  return (
    <div className="relative">
      <Input id="lf-family-id" data-testid="ledger-field-family-id" value={value} autoComplete="off"
        placeholder="e.g. PET0015 or search by name…" className="mt-1.5"
        onChange={(e) => { onType(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {open && opts.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-white shadow-lg" data-testid="ledger-family-id-list">
          {opts.map((f) => (
            <button key={f.family_id} type="button" data-testid={`ledger-family-id-option-${f.family_id}`}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-secondary"
              onMouseDown={(ev) => { ev.preventDefault(); onPick(f); setOpen(false); }}>
              <span className="font-mono text-xs text-navy">{f.family_id}</span> — {f.head_of_family || f.family_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DonorNameField({ value, onType, onPick }) {
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/admin/ledger/donor-picker", { params: { q: value } });
        setOpts(res.data.items || []);
      } catch {
        setOpts([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value, open]);
  return (
    <div className="relative">
      <Input id="lf-party" data-testid="ledger-field-party" value={value} autoComplete="off"
        placeholder="Donor / member name…" className="mt-1.5"
        onChange={(e) => { onType(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)} />
      {open && opts.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-white shadow-lg" data-testid="ledger-donor-list">
          {opts.map((m, i) => (
            <button key={`${m.family_id}-${m.full_name}-${i}`} type="button" data-testid={`ledger-donor-option-${i}`}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-secondary"
              onMouseDown={(ev) => { ev.preventDefault(); onPick(m); setOpen(false); }}>
              {m.full_name} {m.family_id && <span className="text-xs text-muted-foreground">({m.family_id})</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PledgeMonths({ familyId, party, initialMonths, excludeId, onChange }) {
  const [from, setFrom] = useState(initialMonths?.[0] || "");
  const [to, setTo] = useState(initialMonths?.length ? initialMonths[initialMonths.length - 1] : "");
  const [paid, setPaid] = useState([]);

  useEffect(() => {
    if (!familyId && !party?.trim()) { setPaid([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/admin/ledger/pledge-status", { params: { family_id: familyId || "", party: party || "", exclude_id: excludeId || "" } });
        setPaid(res.data.paid_months || []);
        if (res.data.suggested) {
          setFrom((cur) => cur || res.data.suggested);
          setTo((cur) => cur || res.data.suggested);
        }
      } catch {
        setPaid([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [familyId, party, excludeId]);

  const covered = useMemo(() => monthsInRange(from, to), [from, to]);
  const overlap = useMemo(() => covered.filter((m) => paid.includes(m)), [covered, paid]);

  useEffect(() => { onChange(covered, overlap); }, [covered, overlap, onChange]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="lf-pm-from" className="text-xs text-muted-foreground">From month</Label>
          <Input id="lf-pm-from" type="month" data-testid="ledger-pledge-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-9 w-40" />
        </div>
        <div>
          <Label htmlFor="lf-pm-to" className="text-xs text-muted-foreground">To month</Label>
          <Input id="lf-pm-to" type="month" data-testid="ledger-pledge-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-9 w-40" />
        </div>
      </div>
      {covered.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="ledger-pledge-chips">
          {covered.map((m) => {
            const isPaid = paid.includes(m);
            return (
              <span key={m} data-testid={`ledger-pledge-chip-${m}`}
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${isPaid ? "bg-red-100 text-red-700 line-through" : "bg-green-100 text-green-800"}`}>
                {monthLabel(m)}{isPaid ? " · paid" : ""}
              </span>
            );
          })}
        </div>
      )}
      {overlap.length > 0 && (
        <p className="text-xs text-red-700" data-testid="ledger-pledge-overlap">
          {overlap.map(monthLabel).join(", ")} already recorded for this member/family. Adjust the range before saving.
        </p>
      )}
      {covered.length === 0 && (
        <p className="text-xs text-muted-foreground">Choose the first and last month this payment covers (use the same month for a single-month payment).</p>
      )}
    </div>
  );
}

function CategoriesDialog({ open, onClose, categories }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("income");
  const [busy, setBusy] = useState(false);
  const byType = useMemo(() => ({
    income: categories.filter((c) => c.type === "income"),
    expense: categories.filter((c) => c.type === "expense"),
  }), [categories]);

  const add = async () => {
    if (!name.trim()) { toast.error("Enter a category name"); return; }
    setBusy(true);
    try {
      await api.post("/admin/ledger/categories", { name: name.trim(), type });
      toast.success("Category added");
      setName("");
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/admin/ledger/categories/${c.id}`);
      toast.success("Category deleted");
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="ledger-categories-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">Ledger Categories</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" data-testid="category-name-input" />
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-32" data-testid="category-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={add} disabled={busy} className="bg-navy text-cream" data-testid="category-add-btn">Add</Button>
          </div>
          {["income", "expense"].map((t) => (
            <div key={t}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t === "income" ? "Income Categories" : "Expense Categories"}</p>
              <div className="flex flex-wrap gap-2">
                {byType[t].map((c) => (
                  <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-medium text-navy" data-testid={`category-chip-${c.id}`}>
                    {c.name}
                    <button onClick={() => remove(c)} aria-label={`Delete ${c.name}`} data-testid={`category-delete-${c.id}`} className="text-destructive hover:text-destructive/70">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="categories-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OpeningBalanceDialog({ open, onClose, settings, onSaved }) {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && settings) {
      setAmount(String(settings.opening_balance ?? 0));
      setDate(settings.opening_date || "");
    }
  }, [open, settings]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/ledger/settings", { opening_balance: Number(amount || 0), opening_date: date });
      toast.success("Opening balance saved");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="opening-balance-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-navy">Opening Balance</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            The balance the church had when you started keeping records here. Closing Balance = Opening Balance + Total Income − Total Expenses.
          </p>
          <div>
            <Label htmlFor="ob-amount">Opening Balance (₹)</Label>
            <Input id="ob-amount" data-testid="opening-balance-input" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="ob-date">As of Date</Label>
            <Input id="ob-date" data-testid="opening-balance-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="opening-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-navy text-cream" data-testid="opening-save-btn">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
